<template>
    <div>
        <b-navbar id="nav" toggleable="lg" type="dark" variant="info" class="elegant-navbar">
            <b-navbar-brand href="#" class="elegant-brand">Magyar Néprajzi Atlasz</b-navbar-brand> <!-- TODO: add localization -->

            <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

            <b-collapse id="nav-collapse" is-nav>
            <b-navbar-nav>
                <b-button @click="openChooserModal" variant="outline-light" class="nav-selection-btn">
                    <b-icon-map v-if="!questionCode"></b-icon-map>
                    <b-icon-question-circle v-else></b-icon-question-circle>
                    <span v-if="questionCode && questionTitle">{{ questionTitle }}</span>
                    <span v-else-if="selectedMapPageName">{{ selectedMapPageName }}</span>
                    <span v-else>Térképlap / Kérdés választása</span>
                </b-button>
                <b-button @click="openGistaModal" variant="outline-light" class="nav-selection-btn ml-2">
                    <b-icon-bar-chart-line></b-icon-bar-chart-line>
                    <span v-if="selectedGistaAttributeLabel">GISta: {{ selectedGistaAttributeLabel }}</span>
                    <span v-else>GISta tematikus alapréteg</span>
                    <b-icon-x-circle v-if="selectedGistaAttribute" class="ml-2" @click.stop="clearGistaAttribute" title="Tematikus alapréteg kikapcsolása"></b-icon-x-circle>
                </b-button>
            </b-navbar-nav>
            <b-navbar-nav class="ml-auto">
                <b-button @click="showWelcomeModal = true" variant="outline-light" class="mr-2">
                    <b-icon-info-circle></b-icon-info-circle>
                </b-button>
                <b-button v-b-toggle.sidebar-1 v-if="selectedMapPage" variant="outline-light">
                    <b-icon-list></b-icon-list> Jelmagyarázat
                </b-button>
            </b-navbar-nav>
        </b-collapse>
    </b-navbar>
    
    <!-- Navigation Modal with Master-Detail Layout -->
    <b-modal v-model="showNavigationModal" title="Térképlap választása" size="xl" hide-footer class="elegant-modal navigation-modal">
        <!-- Mode Toggle -->
        <div class="mb-3">
            <b-button-group class="w-100">
                <b-button
                    @click="switchToMapPageMode"
                    :variant="displayMode === 'mapPage' ? 'primary' : 'outline-secondary'"
                    class="mode-toggle-btn"
                >
                    <b-icon-map></b-icon-map> Térképlap
                </b-button>
                <b-button
                    @click="switchToQuestionMode"
                    :variant="displayMode === 'question' ? 'primary' : 'outline-secondary'"
                    class="mode-toggle-btn"
                >
                    <b-icon-question-circle></b-icon-question-circle> Kérdés
                </b-button>
            </b-button-group>
        </div>

        <!-- Search Bar -->
        <div class="mb-3">
            <b-form-input
                v-model="searchQuery"
                placeholder="Keresés kategóriákban és térképlapokban..."
                @input="performSearch"
                class="search-input"
            >
                <template #prepend>
                    <b-icon-search></b-icon-search>
                </template>
            </b-form-input>
        </div>

        <!-- Search Results View -->
        <div v-if="searchQuery && searchQuery.length > 0">
            <div v-if="searchResults.length > 0">
                <h6 class="section-title">Találatok ({{ searchResults.length }})</h6>
                <div class="search-results-list">
                    <div
                        v-for="result in searchResults"
                        :key="result.id || result.appArea"
                        @click="selectSearchResult(result)"
                        class="search-result-item"
                    >
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div class="d-flex align-items-center mb-1">
                                    <b-badge
                                        :variant="result.type === 'mapPage' ? 'primary' : 'secondary'"
                                        class="mr-2"
                                    >
                                        {{ result.type === 'mapPage' ? 'Térképlap' : 'Kategória' }}
                                    </b-badge>
                                    <strong>{{ result.displayName }}</strong>
                                </div>
                                <div v-if="result.type === 'mapPage' && result.appArea" class="text-muted small">
                                    Kategória: {{ result.appArea }}
                                </div>
                            </div>
                            <b-icon-arrow-right class="text-muted"></b-icon-arrow-right>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-muted text-center py-4">
                Nincs találat a keresési feltételre: "{{ searchQuery }}"
            </div>
        </div>

        <!-- Map Page Master-Detail View -->
        <div v-else-if="displayMode === 'mapPage'" class="master-detail-container">
            <!-- Master: Categories List -->
            <div class="master-pane">
                <h6 class="section-title">Kategóriák</h6>
                <div class="category-list">
                    <div
                        v-for="area in appAreas"
                        :key="area.appArea"
                        @click="selectCategory(area.appArea)"
                        :class="['category-item', { 'active': internalSelectedAppArea === area.appArea }]"
                    >
                        <b-icon-folder class="category-icon"></b-icon-folder>
                        <span class="category-name">{{ area.appArea }}</span>
                    </div>
                </div>
            </div>

            <!-- Detail: Map Pages List -->
            <div class="detail-pane">
                <h6 class="section-title">
                    Térképlapok
                    <span v-if="mapPages.length > 0" class="text-muted font-weight-normal">({{ mapPages.length }})</span>
                </h6>
                <div v-if="internalSelectedAppArea && mapPages.length > 0" class="map-page-list">
                    <div
                        v-for="page in mapPages"
                        :key="page.id"
                        @click="selectMapPage(page)"
                        :class="['map-page-item', { 'active': internalSelectedMapPage === page.mnaId || internalSelectedMapPage === page.id }]"
                    >
                        <b-icon-map class="map-icon"></b-icon-map>
                        <span class="map-page-name">{{ page.name }}</span>
                        <b-icon-check-circle-fill v-if="internalSelectedMapPage === page.mnaId || internalSelectedMapPage === page.id" class="check-icon"></b-icon-check-circle-fill>
                    </div>
                </div>
                <div v-else-if="internalSelectedAppArea && mapPages.length === 0" class="text-muted text-center py-5">
                    <b-spinner small class="mb-2"></b-spinner>
                    <p>Betöltés...</p>
                </div>
                <div v-else class="text-muted text-center py-5">
                    <b-icon-inbox style="font-size: 2rem; opacity: 0.3;" class="mb-2"></b-icon-inbox>
                    <p>Válasszon kategóriát a térképlapok megjelenítéséhez</p>
                </div>
            </div>
        </div>

        <!-- Question Chooser View -->
        <div v-else-if="displayMode === 'question'" class="master-detail-container">
            <!-- Master: top-level question categories (children of the catalog root) -->
            <div class="master-pane">
                <h6 class="section-title">Kategóriák</h6>
                <div v-if="!questionCategoriesLoaded" class="text-muted text-center py-5">
                    <b-spinner small class="mb-2"></b-spinner>
                    <p>Kategóriák betöltése…</p>
                </div>
                <div v-else-if="questionCategories.length === 0" class="text-muted text-center py-5">
                    <b-icon-exclamation-circle style="font-size: 2rem; opacity: 0.3;" class="mb-2"></b-icon-exclamation-circle>
                    <p>Nincsenek elérhető kategóriák.</p>
                </div>
                <div v-else class="category-list">
                    <div
                        v-for="cat in questionCategories"
                        :key="cat.katalog"
                        @click="selectQuestionCategory(cat)"
                        :class="['category-item', { 'active': internalSelectedQuestionCategory === cat.katalog }]"
                    >
                        <b-icon-folder class="category-icon"></b-icon-folder>
                        <span class="category-name">{{ cat.katalog_nev }}</span>
                    </div>
                </div>
            </div>

            <!-- Detail: questions for the selected category -->
            <div class="detail-pane">
                <h6 class="section-title">
                    Kérdések
                    <span v-if="questionsList.length > 0" class="text-muted font-weight-normal">({{ questionsList.length }})</span>
                </h6>
                <div v-if="questionsLoading" class="text-muted text-center py-5">
                    <b-spinner small class="mb-2"></b-spinner>
                    <p>Kérdések betöltése…</p>
                </div>
                <div v-else-if="internalSelectedQuestionCategory && questionsList.length > 0" class="map-page-list">
                    <div
                        v-for="rf in questionsList"
                        :key="rf.recfields"
                        @click="selectQuestion(rf)"
                        :class="['map-page-item', { 'active': questionCode === rf.recfields_kod }]"
                        :title="rf.leiras || ''"
                    >
                        <b-icon-question-circle class="map-icon"></b-icon-question-circle>
                        <span class="map-page-name">
                            <span class="text-muted small mr-1">{{ rf.recfields_kod }}</span>
                            {{ rf.recfields_nev }}
                        </span>
                        <b-icon-check-circle-fill v-if="questionCode === rf.recfields_kod" class="check-icon"></b-icon-check-circle-fill>
                    </div>
                </div>
                <div v-else-if="internalSelectedQuestionCategory && questionsList.length === 0" class="text-muted text-center py-5">
                    <b-icon-inbox style="font-size: 2rem; opacity: 0.3;" class="mb-2"></b-icon-inbox>
                    <p>Ebben a kategóriában nincs kérdés.</p>
                </div>
                <div v-else class="text-muted text-center py-5">
                    <b-icon-inbox style="font-size: 2rem; opacity: 0.3;" class="mb-2"></b-icon-inbox>
                    <p>Válasszon kategóriát a kérdések megjelenítéséhez</p>
                </div>
            </div>
        </div>

    </b-modal>

    <!-- GISta Thematic Underlay Modal — independent of MNA selection -->
    <b-modal v-model="showGistaModal" title="GISta tematikus alapréteg választása" size="xl" hide-footer class="elegant-modal navigation-modal">
        <div class="text-muted small mb-3">
            A tematikus alapréteg az OSM és az MNA-pontok között jelenik meg, így aktív térképlap vagy kérdéstérkép mellett is bekapcsolható.
            Az adatok forrása a <a href="http://gistahungarorum.abtk.hu" target="_blank" rel="noopener">GISta Hungarorum</a> (HUN-REN BTK), CC BY-NC-SA 4.0.
        </div>
        <div class="master-detail-container">
            <!-- Master: Themes List -->
            <div class="master-pane">
                <h6 class="section-title">Témakörök</h6>
                <div class="category-list">
                    <div
                        v-for="theme in gistaThemes"
                        :key="theme.key"
                        @click="selectGistaTheme(theme)"
                        :class="['category-item', { 'active': internalSelectedGistaTheme === theme.key }]"
                    >
                        <b-icon-bar-chart-line class="category-icon"></b-icon-bar-chart-line>
                        <span class="category-name">{{ theme.label }}</span>
                    </div>
                </div>
            </div>

            <!-- Detail: Attributes List -->
            <div class="detail-pane">
                <h6 class="section-title">
                    Tematikus alaprétegek
                    <span v-if="selectedGistaThemeAttributes.length > 0" class="text-muted font-weight-normal">({{ selectedGistaThemeAttributes.length }})</span>
                </h6>
                <div v-if="internalSelectedGistaTheme && selectedGistaThemeAttributes.length > 0" class="map-page-list">
                    <div
                        v-for="attr in selectedGistaThemeAttributes"
                        :key="attr.key"
                        @click="selectGistaAttribute(attr)"
                        :class="['map-page-item', { 'active': selectedGistaAttribute === attr.key }]"
                    >
                        <b-icon-geo-alt class="map-icon"></b-icon-geo-alt>
                        <span class="map-page-name">{{ attr.label }}</span>
                        <b-icon-check-circle-fill v-if="selectedGistaAttribute === attr.key" class="check-icon"></b-icon-check-circle-fill>
                    </div>
                </div>
                <div v-else class="text-muted text-center py-5">
                    <b-icon-bar-chart-line style="font-size: 2rem; opacity: 0.3;" class="mb-2"></b-icon-bar-chart-line>
                    <p>Válasszon témakört a tematikus alaprétegek megjelenítéséhez</p>
                </div>
            </div>
        </div>
        <div v-if="selectedGistaAttribute" class="mt-3 text-right">
            <b-button variant="outline-secondary" size="sm" @click="clearGistaAttribute">
                <b-icon-x-circle></b-icon-x-circle> Tematikus alapréteg kikapcsolása
            </b-button>
        </div>
    </b-modal>

    <!-- Welcome Modal -->
    <b-modal v-model="showWelcomeModal" title="Üdvözöljük a Magyar Néprajzi Atlasz online kiadásában!" size="xl" class="elegant-modal welcome-modal" @hidden="onWelcomeModalClose">
        <div class="welcome-content">
            <h6 class="authors">Borsos Balázs – Velősy Péter – Ament-Kovács Bence</h6>
            <h5 class="subtitle">A Magyar Néprajzi Atlasz online verziója elé</h5>

            <p>Miután a nemzetközi néprajzi vizsgálatokban már a 19. században nyilvánvalóvá vált, hogy a kultúra jelenségeinek eredetét és történetét nem lehet sem feltárni, sem értelmezni anélkül, hogy a kultúra térbeli voltát figyelembe ne vennék, kézenfekvő lépés volt a kartográfiai módszer kifejlesztése, mert bármely jelenség térbelisége legkönnyebben az arra jellemző adatok térképre vitelével fogható meg. A térszemlélet elvi megalapozása után a néprajzi kartográfia egyre inkább a módszeresség igényével lépett fel és a 20. század első felében megszületett a néprajzi atlaszok ideája. A néprajzi atlaszok koncepciója ugyan változásokon ment át az utóbbi 100 esztendőben, de alapvetően minden atlasz abból a meggondolásból indul ki, hogy egy adott nép kultúrájának feltárását és megértését megkönnyítheti, ha nemcsak egyes, hanem sok kulturális jelenséget viszünk térképre, amelyeket azonos elvek alapján gyűjtöttünk, amelyek a kultúra részterületeit minél teljesebben képviselik, s amelyek térképi ábrázolása az összevetés és összehasonlítás érdekében azonos elvek alapján történik. Német-, illetve Lengyelországban már 1920-as évek elején megfogalmazódott a néprajzi atlaszok készítésének terve. Az első, néprajzi szempontból is fontos munkát Jaberg és Jud 1928-tól publikált olasz és dél-svájci nyelvatlasza jelentette, amely az olasz nyelvterületen egyenletesen eloszlott több száz kutatópont anyagának földolgozásával igazolta, hogy hasonló módszertannal néprajzi atlasz is létrehozható. Az első igazi, tematikailag és méretében jól körülhatárolt, be is fejezett néprajzi atlaszt a két háború között Lengyelországban készítette el Kazimierz Moszynski és csapata, munkájuk azonban aránylag észrevétlen maradt. Az európai atlaszmunkálatokra jóval nagyobb hatást gyakorolt az 1930 és 1935 közt publikált, több mint 20.000 gyűjtőponton, levelezők által kitöltött 243 kérdést tartalmazó kérdőíveket részben feldolgozó német néprajzi atlasz (ADV) első, 120 térképből álló folyama. Bár egyes kérdésköreit és a feldolgozás szempontjait illetően nemigen tudta magát függetleníteni kora nemzetiszocialista közszellemétől. Számos fogyatékossága ellenére azonban az ADV bebizonyította a néprajzi atlaszok jogosultságát, és példaként szolgált a többi nemzet atlaszkészítői számára, akik már igyekeztek tanulni ennek hibáiból.</p>

            <p>A német minta alapján egy néprajzi atlasz készítése iránti érdeklődés Magyarországon Schwarz Elemér nevéhez köthetően már az 1920-as évek végén megjelent, de a kezdeményezést csak jó tíz évvel később öntötte formába Gunda Béla a Magyar Néprajzi Társaságban, 1939 októberében tartott előadásában. A szállításra vonatkozóan 20 témában kérdőívet is kidolgozott, és továbbiakat is tervezett. Az első vonatkozó témalapokat a Magyarságtudományi Intézet költségén nyomtatták és küldték ki. 700 felvételi ponton 1940-ben és 1942-ben már a gyűjtés is megindult, ám a munkálatok – mint Európában másutt is – a háború miatt megszakadtak. Az 1941 végéig visszaérkező, a Néprajzi Múzeumban őrzött kérdőívek feldolgozása napjainkig sem történt meg.</p>

            <p>Jelentős ilyen irányú tevékenység Európában, ekkoriban csupán a harcoktól nem pusztított Svédországban és Svájcban folyt. A skandináv ország elsősorban a gyűjtésre koncentrált: a harmincas évek végén elkészült tervek ellenére az anyagi kultúrával foglalkozó kötet csak 1957-ben jelent meg, a szellemi kultúrát bemutató második rész pedig további 20 évvel később, 1976-ban látott napvilágot. Svájcban az atlaszmunkálatok a svájciaknak tulajdonított rendszerességgel időben szervezetten és megszakítás nélkül folytak. Mivel a próbagyűjtés tanulságai a levelezőmódszer ellen szóltak, mindössze 11 kutató közel 400 kutatóponton, 1937 és 1942 között, összesen 150 témában folytatott gyűjtést, amelyet 1950-től 1983-ig folyamatosan meg is jelentettek. A térképek mellé az adatokat magyarázó, kiegészítő kommentárkötetek is készültek. Nem véletlenül tartja Barabás Jenő a svájci atlaszt kiemelkedő jelentőségűnek: „ez az első megfelelő bevezetéssel, magyarázó résszel együtt megjelent, komplett atlasz, amelynek szerkesztési munkálatainál elejétől kezdve következetesen végrehajtották a meghatározott célkitűzéseket." Egy minden szempontból kiérlelt atlasz elkészítésének nehézségeit jól tükrözi, hogy hasonlóan nagy lapszámú, sok kulturális aspektust átfogó és kommentárjaiban is kidolgozott atlasz igazából azóta sem született.</p>

            <p>Pedig az európai atlaszmunkálatok az 1950-es évek második felében új lendületet kaptak, amelynek az egyes nemzeti atlaszok elkészítése mellett egy egységes elvek alapján szerkesztett Európai Néprajzi Atlasz elkészítése is célja volt. A szinkron módon, és kifejezetten térképgyűjtemény elkészítése céljából, nemzeti vagy országos szinten, nagy infrastruktúrával gyűjtött anyagokból összeállított „igazi" néprajzi atlaszok készítése (a 21. századból visszatekintve már látszik) elsődlegesen közép-európai kérdésfelvetés és projekt maradt. A svéd és svájci atlaszon kívül részben vagy teljesen csak a magyar, a német, az osztrák, a lengyel, a finn és a szlovák atlasz készült el. A holland és belgiumi flamand területeket felölelő atlasz – mivel kizárólag a hiedelmekkel foglalkozik – egyoldalú maradt; a cseh atlasz, noha néprajzinak nevezik, kizárólag történeti adatokat tartalmaz, akárcsak a baltikumi; a jugoszláv és görög kezdeményezés néhány térkép publikálása után elhalt. A nemzetközi atlaszkonferenciákon megjelentek ugyan norvég, dán, bolgár, albán, portugál, francia, ír, brit és más nemzetiségű kutatók, de ha be is számoltak néprajzi atlaszok kezdeményezéséről, azok a terv formájánál többre nemigen jutottak. A hatalmas erőket mozgató előkészítő tevékenység, a más tudományágakra és az Európán kívüli területekre is kiterjedő érdeklődés ellenére az európai atlasz készítése is kudarcba fulladt: a munka mozgatói elhunytak, vagy érdeklődésüket vesztették, s az új generáció már máshova helyezte a néprajztudomány súlypontját. Az Európai Néprajzi Atlaszból mindössze egyetlen, az évi tűzgyújtást ábrázoló térkép készült el, s az is jórészt a korábbi nemzeti atlaszok adatbázisát használta. Az etnokartográfia intézményesített együttműködése is kimúlni látszik. Bár az 1990-es években a még készülő néprajzi atlaszok (cseh, magyar, szlovák) bizonyos fokig továbblendítették a munkát. Az etnokartográfusok megváltozott névvel s megváltozott prioritással folytatták tevékenységüket, a súlypontot bizonyos témák térképészeti módszerekkel történő feldolgozására helyezve, a 21. évszázadra azonban már csak a hattyúdal maradt. 2000-ben, a szlovákiai Komáromban tartott 12. atlaszkonferencián az utolsó „nagy öregek" (H. L. Cox, Alexander Fenton) lemondtak a munkaközösség vezetéséről, s annak felélesztésére való kísérlet 2010-ben, Szegeden, lényegében visszhangtalan maradt.</p>

            <p>Ugyanakkor a nemzetközi kapcsolatháló lazulása ellenére a nemzeti néprajzi atlaszokat készítő tevékenység nem szűnt meg. 2000-ben megjelent a történeti adatokat tartalmazó Cseh Néprajzi Atlasz 3. kötete és német nyelven a Finn Néprajzi Atlasz folklórkötete, 2003-ban pedig megindult az ötkötetesre tervezett Román Néprajzi Atlasz publikálása is. És itt kell megemlítenünk (bár nem nemzeti, hanem regionális kitekintésű) a 2002-ben napvilágot látott A Vajdasági Magyarok Néprajzi Atlaszát is, amely az idők szavát követve már nem papírra, hanem teljes egészében digitális adathordozóra készült. Ez a vállalkozás már átvezet bennünket a Magyar Néprajzi Atlasz készítéséhez.</p>

            <p>Az atlaszmunkálatok felvirágzása az ötvenes évek közepén a magyar néprajztudományt is elérte. Bár a munka nem Gunda korábbi koncepciója mentén haladt, a folyamatosságot biztosította, hogy Gunda lett a magyar atlasz 1955-ben megalakult szerkesztőbizottságának a vezetője. A következő években azonban az atlaszmunkák (és a magyar néprajzi kartográfia) meghatározó személyiségévé Barabás Jenő vált, s a kész atlasz végül az ő főszerkesztésében jelent meg. Hosszú és kiterjedt tudományos vita eredményeképpen 1955 és 1959 között elkészült a négykötetes gyűjtőfüzet és a gyűjtési útmutató. A 150 anyagi és az 50 szellemi kultúrára vonatkozó témakörben fölsorolt több mint 1000 kérdés az 1960-ban még élő legöregebb adatközlők tudásának fölhasználásával az 1900 körüli kulturális képet igyekezett megrajzolni, de a gyűjtőfüzetek rákérdeztek a recens adatokra, illetve bizonyos jelenségek időbeli változására is. Az útmutató szerint átlagosan 5–6 adatközlő megszólaltatására volt szükség falvanként, de ez a szám egyes településeken akár a 20-at is elérhette.</p>

            <p>Az 1959-ben megindult gyűjtés kezdetben csak Magyarországon folyt, ám már akkor tervezték, hogy a munkát (például a lengyel, vagy a szlovák atlasztól eltérően, amely a politikai határok közötti területre korlátozódott) a későbbiekben kiterjesztik a teljes magyar nyelvterületre a Felső-Őrségtől Moldváig. A létező szocializmus korában figyelemreméltó döntés eredményeképpen fölmerülő munkát a különböző tudományos csereegyezmények keretében igyekeztek elvégezni: a korabeli viszonyokat és a korabeli terepmunka nehézségét jól jelzi, hogy a leghiányosabban a romániai gyűjtőpontok adatait sikerült összeszedni. Kezdetben 250 magyarországi és mintegy 100 külföldi gyűjtőponttal számoltak, amelyek száma a gyűjtés tapasztalatainak következtében 253 + 164 = 417-re nőtt. A határon túli kutatópontokból (a mai országokat sorolva) Romániában 100, Szlovákiában 35, Szerbiában 18, Ukrajnában 6, Horvátországban 3, és végül Ausztriában és Szlovéniában 1–1 található. Az atlasz szerkesztői arra törekedtek, hogy a kijelölt kutatópontok történeti, társadalmi, kulturális, gazdasági és vallási tekintetben egyaránt vidékük reprezentatív típusának legyenek tekinthetők. A reprezentatív minta meghatározásánál azt is figyelembe vették, hogy a település lakossága hosszabb idő (több generáció, de legalább 100 év) óta helyben élő legyen. Átlagosan 400 km2-ként (egymástól 20–25 km távolságban) választottak egy települést, az aprófalvas területeken sűrűbb, a nagyhatárú mezővárosok esetében ritkább ez a háló. Ezt a rendszert a határok mentén élő tömbmagyarságra is kiterjesztették, ám a szórványban élők esetében a kutatottság, illetve a helyi kapcsolatok, viszonyok is befolyásolták a döntést, melyik település került végül be a mintába. Az alapgyűjtést egy 12–15 fős törzsgárda mellett további 65–70 kutató végezte, s ez a szám a helyi kezdeményezések, a kiegészítő és pótgyűjtések során összesen 159-re nőtt, ez a munka szinte a teljes magyar néprajzkutató gárdát megmozgatta, Andrásfalvy Bertalantól Voigt Vilmosig. A résztvevők nagy száma mindazonáltal nem vált az anyag egységességének javára, de magyarázatul szolgál, hogy 1967-ig, az MTA Néprajzi Kutatócsoportjának megalakulásáig az atlaszmunkálatoknak (a legtöbb nemzettől eltérően) nem volt igazi intézményi háttere, A munkálatok adminisztrálását 1962–1967 között az ELTE Néprajzi Intézetéhez kapcsolódó Tanszéki Akadémiai Kutatócsoport végezte. A Magyar Néprajzi Atlasz gyűjteményét és archívumát azonban a Néprajzi Múzeumban működő akadémiai kutatói részlegben helyezték el. Az anyagot végül csak 1969-ben szállították át a Néprajzi Kutatócsoport várbeli telephelyére.</p>

            <p>A gyűjtés első szakasza 1966-ban zárult. Ezután (kizárólag kézi eszközökkel) megkezdődött az anyag feldolgozása, melynek során 2000 munkatérkép készült. Ekkor jelent meg az első mutatvány az atlasz anyagából, ám a földolgozás további pótgyűjtések szükségességét is bizonyította, amelynek oroszlánrészét 1968–1970 közt az egyes lapok készítői végezték. A földolgozás következő szakaszában (1972-ig) döntöttek a készítendő lapokról, és meghatározták az alaptérkép jellemzőit. A német, illetve lengyel atlasszal megegyezően 1:2.000.000 méretarányú alaptérképre a jelentősebb folyók alkotta vízhálózat, valamint az akkori politikai határok mellett egy négyzethálót nyomtattak, és a települések helyzetét a térképen e négyzetháló alapján, két szintben definiálták.</p>

            <p>Az 1970-80-as években anyagi és tudományszervezési okokból a munkálatok lelassultak. Az első három kötet kiadására csak 1987-ben került sor, ám ezt – számos más nemzettől eltérően – hamarosan követték a többiek: a 9 kötetben összesen 634 térképlapot tartalmazó atlasz megjelentetése öt évvel később, 1992-ben lezárult. A térképek elkészítésében végül összesen 12 néprajzkutató vett részt, a legtöbb lapot Paládi-Kovács Attila (126), Morvay Judit (74), Kisbán Eszter és Szolnoky Lajos (70–70) készítette. Az alaptérképre a települések fölé az egymást követő témacsoportokban eltérő színnel (zöld, barna, piros) nyomtatták az éppen térképezett kulturális jelenség ott jellemző változatának/változatainak jelét. A térkép címe és a jelkulcs magyar és német nyelven került a lapra. A nemzetközi atlaszkiadványokban általános követelménynek tartott magyarázó kötet(ek) azonban sajnos nem készült(ek) el. A tervek szerint a térképek interpretálásához szükséges tényanyagot tartalmazták volna, valamint – a filológiai kifejtést más kiadványokra hagyva – egy aránylag rövid térképértelmezést. Némi képet alkothatunk a tervről a mutatványanyag alapján, amelyben néhány lap kommentárját elkészítették. Miután azonban az atlasz szerkesztői közül senki nem él már és a 12 szerző közül sincsenek már köztünk hatan, és a néprajztudomány érdeklődésének súlypontja is megváltozott, az atlasz valószínűleg már végleg így befejezetlenül marad. A vállalkozás ennek ellenére számos további kutatási lehetőséget tartogat. Bár – mint a gyűjtőfüzetek tematikája is mutatja – a Magyar Néprajzi Atlasz a népi kultúra aspektusait tekintve nem igazán kiegyensúlyozott, más nemzetek atlaszaihoz képest gazdagnak tekinthető, és a népi kultúra 1900 körüli térbeli szerkezetéről így is teljes áttekintést ad.</p>

            <h6 class="section-heading">A Magyar Néprajzi Atlasz digitalizálása</h6>

            <p>Az MNA több más európai néprajzi atlasszal összehasonlítva azért is alkalmas a digitalizálásra, mert, hasznosítva a korábban megjelent atlaszok tapasztalatait, egységes koncepció által készült, mind a kutatott jelenségeket, mind az időhatárt, mind a gyűjtőpontokat illetően, az adatokat szakemberek és a terepen gyűjtötték, és az eredményt többé-kevésbé egységes térképi módszerekkel ábrázolták. A Magyar Néprajzi Atlasz, noha számos és változatos kulturális jelenség térképi ábrázolását tartalmazza, természetesen nem mutatja be a magyar népi kultúrát a maga teljességében. Az ábrázolt kulturális jelenségek korlátozott száma a kisebb probléma. A 634 térképlap igen tekintélyes szám (az eddig megjelent néprajzi atlaszok közül a legnagyobb), és nyilvánvalóan valahol határt kellett húzni. A 634 lap azonban nem 634, csupán 627 kulturális jelenség helyi variációinak térbeli eloszlását mutatja be. Néhány jelenség valamennyi változata ugyanis nem fért volna el egyetlen térképen, ezeket több lapra bontva ábrázolták. Más lapokon ugyanakkor több, részben összetartozó kulturális jelenség együtt szerepel, a részletesebb bemutatás érdekében. Akadnak lapok, amelyeken az előző lapon bemutatott kulturális jelenség egyik-másik vonásának részletezése található. A lapok és a tényleges jelenségek száma között azonban a különbség – különösen ekkora laplétszámnál – elhanyagolhatóan kicsi.</p>

            <p>Az atlasz az adott kulturális jelenséget elméletileg a 417 gyűjtőponton ábrázolja. Ez tört része a teljes magyar nyelvterület 10.000-t meghaladó települései számának. Több kutatópont lehetővé tenné az egyes területi csoportok részletezőbb elhatárolását, de a világos és pontos térképi ábrázolás rovására. Nagyobb probléma, hogy a térképező módszer sajátosságaiból adódóan a magyar népi kultúrának nem minden aspektusa szerepel az atlasz lapjain, s amelyikek szerepelnek, azok sem egyforma mennyiségű lapon és egyforma részletezettségben. A Magyar Néprajzi Atlaszból a zsákmányolás, a kereskedelem, a tánc, a játék, a népi vallásosság és a díszítőművészet témája hiányzik, alig szerepel a szövegfolklór, a zene, a népi gyógyítás és a településszerkezet témája is, ezzel szemben túlreprezentáltnak tekinthető a földművelés (17%) és a táplálkozás (22%). A többi területet illetően némileg kiegyensúlyozottabb a kép: az állattartás 9%, az építkezés és az életfordulók 8-8% a közlekedés–szállítás, a viselet, a szokások és a néphit–mágia 6-6%, társadalom 5% arányban van jelen, de aránylag kevés a háziipart–kézművességet (3%), az életmódot (2%) és a rokonsági viszonyokat (2%) bemutató lapok száma.</p>

            <p>A hiányokat bizonyos fokig magyarázza a kérdőív összeállítása idejére jellemző politikai helyzet (népi vallásosság kimaradása), az adott téma sajátossága, hogy nem általános elterjedésű, hanem perszonális és lokális sűrűsödési pontjai vannak, és terjedési dinamikája is eltérő (szövegfolklór, díszítőművészet elhagyása), illetve a korabeli tudomány súlypontjai (népi játékok kimaradása). A népzene és néptánc adatok hiánya azzal is magyarázható, hogy ezek gyűjtése olyan speciális képzettséget igényel, amellyel az etnográfusok többsége nem rendelkezik, ugyanakkor ilyen jellegű gyűjtést akkor és azóta is végeznek ezzel foglalkozó szakemberek.</p>

            <p>A Magyar Néprajzi Atlasz alapvetően az 1900 körüli kulturális képet mutatja be, lapjainak egy része más időmetszetekben ábrázolja a jelenségeket. A gyűjtés során szempont volt a recens (1960 körüli) kulturális jelenségek föltárása is, a megjelent lapok közül mégis mindössze 11 foglalkozik csupán ezzel az időmetszettel. (További két lap /440, 526/ egyszerre ábrázol 1900-as és 1960-as adatokat.) A térképcímekben azonban a gyűjtött adatokból kirajzolódó kép miatt számos más időmegnevezés is van: a 19. század második fele (1), a 19. század vége (4), 20. század eleje (3), a 20. század első harmada (1), 20. század (1) és 45 lap címében a 20. század első fele megnevezés szerepel. Összesen 13 lap pedig egy adott kulturális jelenség első előfordulása vagy eltűnése/megszűnése idejét rögzíti, illetve egy jelenség időbeli változásával foglalkozik. Ezek alapok fontos információt adnak a népi kultúra teróleti megoszlást illetően is, mert Barabás Jenő véleménye szerint „bizonyos táji különbségek semmi másnak nem tekinthetők, csupán időbeli különbözőségnek".</p>

            <p>Az MNA számos térképen bizonyos kulturális jelenségek elnevezését rögzíti: az atlasz térképeinek több mint 28 %-a ilyen lap, és még akkor is 24 % marad, ha kivesszük a rokonszólítás, gyerekszólítás és állatirányító szavak változatait mutató térképeket. Ezzel az aránnyal az MNA az európai atlaszok közt élenjár.</p>

            <p>A Magyar Néprajzi Atlasz, mint adatbázis digitalizálása kezdetén (az 1990-es évek közepén) rögtön szembesültünk azzal a problémával, hogy az általában használt adatbázis-kezelő és adatelemző programok használhatósága korlátozott. Azok használata esetében ugyanis egy létező programnak megfelelően töltjük ki az adatbázist, vagyis azokat az adatokat keressük és használjuk fel, amelyek a program kínálta feldolgozásra alkalmasak, és a következetes rendszerbe beilleszthetők. A Magyar Néprajzi Atlasz készítése idején azonban a számítógépes feldolgozás lehetőségét még nem vették tekintetbe, és az atlasz rendszerében következetlenségek tapasztalhatók. Ha tehát az atlasz adatait a maguk teljességében használni kívánjuk, meg kell fordítani a prioritási sorrendet, és nem az adatokat kell a programhoz illeszteni, hanem az adatok jellegének megfelelő programot kell készíteni, amely kezelni tudja az atlasz következetlenségeit is.</p>

            <p>Az adatbevivő és adatelemző programot Breiner László Gábor programozó matematikus készítette, és fejlesztette folyamatosan, ahogy az adatok elemzése illetve térképre vitele során új és új problémák merülnek föl. A fejlesztésben nagy ugrást jelentett, amikor a korábban külön-külön elkészített adatbázis-kezelő (EthnoMap) és adatbázis elemző (Map CA) programokat összefogva megszületett a számos további előnnyel járó EthnoMap Tools (EM Tool) program. Az atlasz térképeinek digitalizálására még az EthnoMap programot használtuk. Így létrejött a Magyar Néprajzi Atlasz digitalizált változata, amelynek az eredetihez képest számos előnye van. Egyrészt az anyag kilenc kötetnyi, összesen 20 cm vastag és több kilónyi térkép helyett elfér egyetlen adathordozón, másrészt egy felhasználói programrész segítségével az atlasz adatbázisát egyéb szempontok alapján is rendezni tudjuk.</p>

            <p>Az adatbázis tehát 417 elemre (településre) és 634 változóra (térképlapra) készült el. Az adatmátrixba a települések térképi koordinátáiknak megfelelően kerültek be. Az adatbevivő program úgy készült, hogy az egyes térképlapok esetében az elemek számát meg lehetett növelni. Így rögzíteni lehetett az adatbázisban, ha egy bizonyos településen az adott kulturális jelenség több változata is megtalálható. Ilyen számos térképlapon előfordul, következésképpen az adatbázis az eredetileg tervezettnél jóval nagyobb lett, mintegy 400.000 adathelyet tartalmaz. E hatalmas mennyiség miatt három évbe telt az adatbázis kitöltése és az adatok ellenőrzése. Az adatok megjelenítésére készült az EmTool program részváltozata, amelyet Breiner László Gábor tragikusan korai halála után Velősy Péter néprajzkutató informatikus fejlesztett tovább.</p>

            <div class="mt-4 pt-3 border-top">
                <p class="mb-3"><strong>A térképlapok megtekintéséhez kattintson a "Térképlap választása" gombra a felső menüsorban.</strong></p>
            </div>
        </div>

        <template #modal-footer>
            <div class="w-100 d-flex justify-content-between align-items-center">
                <b-form-checkbox v-model="dontShowWelcomeAgain" class="dont-show-checkbox">
                    Ne jelenjen meg újra
                </b-form-checkbox>
                <b-button variant="primary" @click="closeWelcomeModal">
                    Bezárás
                </b-button>
            </div>
        </template>
    </b-modal>
    </div>
</template>

<script>
export default {
  props: [
      'appAreas',
      'mapPages',
      'selectedAppArea',
      'selectedMapPage',
      'questionCode',
      'questionTitle',
      'gistaThemes',
      'selectedGistaAttribute'
  ],
  data() {
      return {
          internalSelectedAppArea: this.selectedAppArea,
          internalSelectedMapPage: this.selectedMapPage,
          internalSelectedGistaTheme: null,
          showNavigationModal: false,
          showQuestionModal: false,
          showGistaModal: false,
          showWelcomeModal: false,
          dontShowWelcomeAgain: false,
          searchQuery: "",
          searchResults: [],
          displayMode: this.questionCode ? 'question' : 'mapPage',
          // "Kérdések tallózása" state (lazy-loaded on first switch to question mode)
          apiUrl: process.env.VUE_APP_API_URL,
          questionCategories: [],
          questionCategoriesLoaded: false,
          internalSelectedQuestionCategory: null,
          questionsList: [],
          questionsLoading: false
      }
  },
  mounted() {
      // Check if we should show the welcome modal
      const dontShow = localStorage.getItem('mna_dontShowWelcome');
      if (!dontShow) {
          this.showWelcomeModal = true;
      }
  },
  computed: {
      selectedMapPageName() {
          if (!this.selectedMapPage || !this.mapPages || !this.mapPages.length) {
              return null;
          }
          const page = this.mapPages.find(p => {
              // Convert both to string for comparison to handle type mismatches
              return String(p.mnaId) === String(this.selectedMapPage);
          });
          return page ? page.name : null;
      },
      selectedGistaThemeAttributes() {
          if (!this.internalSelectedGistaTheme || !this.gistaThemes) return [];
          const theme = this.gistaThemes.find(t => t.key === this.internalSelectedGistaTheme);
          return theme ? theme.attributes : [];
      },
      selectedGistaAttributeLabel() {
          if (!this.selectedGistaAttribute || !this.gistaThemes) return null;
          for (const theme of this.gistaThemes) {
              for (const attr of (theme.attributes || [])) {
                  if (attr.key === this.selectedGistaAttribute) return attr.label;
              }
          }
          return this.selectedGistaAttribute;
      }
  },
  watch: {
      selectedAppArea(newVal) {
          this.internalSelectedAppArea = newVal;
      },
      selectedMapPage(newVal) {
          this.internalSelectedMapPage = newVal;
      },
      questionCode(newVal) {
          // Update display mode based on whether we have a question code
          if (newVal) {
              this.displayMode = 'question';
          } else if (this.selectedMapPage) {
              this.displayMode = 'mapPage';
          }
      },
      showNavigationModal(isOpen) {
          // Clear search when modal is closed
          if (!isOpen) {
              this.searchQuery = "";
              this.searchResults = [];
          }
      }
  },
  methods: {
      openChooserModal() {
          // Both map-page and question chooser views live inside the same
          // navigation modal; the mode toggle at the top switches between them.
          this.showNavigationModal = true;
          if (this.displayMode === 'question') {
              this.loadQuestionCategoriesIfNeeded();
          }
      },
      performSearch() {
          if (!this.searchQuery || this.searchQuery.trim().length === 0) {
              this.searchResults = [];
              return;
          }
          
          const query = this.searchQuery.toLowerCase().trim();
          const results = [];
          
          // Search in appAreas
          this.appAreas.forEach(area => {
              let matches = false;
              
              // Search in appArea name
              if (area.appArea && area.appArea.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              // Search in description if available
              if (area.description && area.description.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              // Search in any other text fields that might exist
              if (area.name && area.name.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              if (matches) {
                  results.push({
                      type: 'appArea',
                      appArea: area.appArea,
                      displayName: area.appArea,
                      description: area.description || area.name,
                      id: area.appArea
                  });
              }
          });
          
          // Search in mapPages
          this.mapPages.forEach(page => {
              let matches = false;
              
              // Search in page name
              if (page.name && page.name.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              // Search in description if available
              if (page.description && page.description.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              // Search in appArea if available
              if (page.appArea && page.appArea.toLowerCase().includes(query)) {
                  matches = true;
              }
              
              if (matches) {
                  results.push({
                      type: 'mapPage',
                      id: page.id,
                      mnaId: page.mnaId,
                      displayName: page.name,
                      description: page.description,
                      appArea: page.appArea
                  });
              }
          });
          
          this.searchResults = results;
      },
      
      selectSearchResult(result) {
          if (result.type === 'appArea') {
              // For appArea results, just set the selectedAppArea
              this.internalSelectedAppArea = result.appArea;
              this.$emit('update:selectedAppArea', result.appArea);
          } else if (result.type === 'mapPage') {
              // For mapPage results, set both selectedAppArea and selectedMapPage
              this.internalSelectedAppArea = result.appArea;
              this.internalSelectedMapPage = result.mnaId || result.id;
              this.$emit('update:selectedAppArea', result.appArea);
              this.$emit('update:selectedMapPage', result.mnaId || result.id);
              // Close modal after selecting a map page
              this.showNavigationModal = false;
          }

          this.searchQuery = "";
          this.searchResults = [];
      },

      selectCategory(category) {
          this.internalSelectedAppArea = category;
          this.$emit('update:selectedAppArea', category);
      },

      selectMapPage(page) {
          this.internalSelectedMapPage = page.mnaId || page.id;
          this.$emit('update:selectedMapPage', page.mnaId || page.id);
          this.showNavigationModal = false;
      },

      closeWelcomeModal() {
          this.showWelcomeModal = false;
      },

      onWelcomeModalClose() {
          if (this.dontShowWelcomeAgain) {
              localStorage.setItem('mna_dontShowWelcome', 'true');
          }
      },

      switchToMapPageMode() {
          this.displayMode = 'mapPage';
          this.searchQuery = "";
          this.searchResults = [];
      },

      switchToQuestionMode() {
          this.displayMode = 'question';
          this.searchQuery = "";
          this.searchResults = [];
          this.loadQuestionCategoriesIfNeeded();
      },

      async loadQuestionCategoriesIfNeeded() {
          if (this.questionCategoriesLoaded) return;
          try {
              const response = await fetch(`${this.apiUrl}/ethnography/categories/tree`);
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const tree = await response.json();
              // Use the direct children of the catalog root (T.F.A → "Atlasz") as
              // the master list — these are the top-level subjects of the atlas.
              this.questionCategories = (tree && Array.isArray(tree.children)) ? tree.children : [];
          } catch (err) {
              console.error('Failed to load question categories:', err);
              this.questionCategories = [];
          } finally {
              this.questionCategoriesLoaded = true;
          }
      },

      async selectQuestionCategory(cat) {
          this.internalSelectedQuestionCategory = cat.katalog;
          this.questionsList = [];
          this.questionsLoading = true;
          try {
              const response = await fetch(`${this.apiUrl}/ethnography/categories/${cat.katalog}/recfields`);
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const data = await response.json();
              this.questionsList = (data && Array.isArray(data.recfields)) ? data.recfields : [];
          } catch (err) {
              console.error('Failed to load recfields for category', cat.katalog, err);
              this.questionsList = [];
          } finally {
              this.questionsLoading = false;
          }
      },

      selectQuestion(rf) {
          this.$emit('update:questionCode', rf.recfields_kod);
          this.$emit('update:questionTitle', rf.recfields_nev);
          this.showNavigationModal = false;
      },

      openGistaModal() {
          this.showGistaModal = true;
          // Pre-select the theme containing the currently active attribute, if any
          if (this.selectedGistaAttribute && this.gistaThemes) {
              for (const theme of this.gistaThemes) {
                  if ((theme.attributes || []).some(a => a.key === this.selectedGistaAttribute)) {
                      this.internalSelectedGistaTheme = theme.key;
                      break;
                  }
              }
          }
      },

      selectGistaTheme(theme) {
          this.internalSelectedGistaTheme = theme.key;
      },

      selectGistaAttribute(attr) {
          this.$emit('update:selectedGistaAttribute', attr.key);
          this.showGistaModal = false;
      },

      clearGistaAttribute() {
          this.$emit('update:selectedGistaAttribute', null);
      }

  }
}
</script>

<style>
    /* Elegant Navbar - Museum Aesthetic */
    .elegant-navbar {
        background: linear-gradient(to right, #0f766e, #14b8a6) !important;
        padding: 0.875rem 2rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    }

    .elegant-brand {
        font-weight: 600;
        font-size: 1.25rem;
        letter-spacing: -0.01em;
        color: white !important;
    }

    #nav .custom-select {
        margin: 0 0.5rem;
        min-width: 250px;
        background-color: white;
        border: 1px solid rgba(255, 255, 255, 0.25);
        border-radius: 0.375rem;
        font-weight: 500;
        font-size: 0.9375rem;
        transition: all 0.25s ease;
        padding: 0.625rem 2rem 0.625rem 0.875rem;
        line-height: 1.5;
        height: auto;
        white-space: normal;
        overflow: visible;
    }

    #nav .custom-select:hover {
        background-color: white;
        border-color: rgba(255, 255, 255, 0.4);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    }

    #nav .custom-select:focus {
        border-color: rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
        background-color: white;
    }

    #nav .btn {
        font-weight: 500;
        border-radius: 0.375rem;
        padding: 0.5rem 1rem;
        transition: all 0.25s ease;
        font-size: 0.9375rem;
    }

    #nav .btn-outline-light {
        border: 1.5px solid rgba(255, 255, 255, 0.85);
        background: transparent;
    }

    #nav .btn-outline-light:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    #nav .btn-outline-light svg {
        font-size: 1.125rem;
    }

    #nav .btn:not(.btn-outline-light) {
        background: white;
        color: #0f766e;
        border: none;
        font-weight: 500;
    }

    #nav .btn:not(.btn-outline-light):hover {
        background: #fefdfb;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    }

    /* Elegant Search Results */
    .search-result-item {
        cursor: pointer;
        transition: all 0.25s ease;
        border-radius: 0.375rem;
        margin-bottom: 0.5rem;
        border: 1px solid #e2e8f0;
        background: white;
    }

    .search-result-item:hover {
        background-color: #faf9f7;
        border-color: #0f766e;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
    }

    .search-result-item:active {
        background-color: #f5f4f1;
    }

    /* Elegant Modal Styling */
    .elegant-modal .modal-header {
        background: #faf9f7;
        color: #0f172a;
        border-bottom: 1px solid #e2e8f0;
        padding: 1.25rem 1.5rem;
    }

    .elegant-modal .modal-title {
        font-weight: 600;
        color: #0f766e;
        letter-spacing: -0.01em;
        font-size: 1.125rem;
    }

    .elegant-modal:not(.navigation-modal) .modal-body {
        padding: 1.75rem;
        background: white;
    }

    .elegant-modal .form-control {
        border: 1px solid #cbd5e1;
        border-radius: 0.375rem;
        padding: 0.625rem 0.875rem;
        font-size: 0.9375rem;
        transition: all 0.25s ease;
    }

    .elegant-modal .form-control:focus {
        border-color: #0f766e;
        box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
    }

    .elegant-modal .badge {
        padding: 0.35em 0.7em;
        font-weight: 600;
        border-radius: 0.25rem;
        font-size: 0.8125rem;
    }

    .elegant-modal .badge-primary {
        background-color: #0f766e;
        color: white;
    }

    .elegant-modal .badge-secondary {
        background-color: #64748b;
        color: white;
    }

    /* Navigation Selection Button */
    .nav-selection-btn {
        font-weight: 500;
        padding: 0.5rem 1.25rem;
        display: inline-flex;
        align-items: center;
        max-width: 600px;
    }

    .nav-selection-btn svg {
        margin-right: 0.5rem;
        flex-shrink: 0;
    }

    .nav-selection-btn span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* Navigation Modal Enhancements */
    .navigation-modal .modal-dialog {
        max-width: 900px;
    }

    .navigation-modal .modal-body {
        padding: 1.5rem;
        max-height: calc(100vh - 200px);
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    /* Search Input */
    .search-input {
        border: 1px solid #cbd5e1;
        border-radius: 0.375rem;
        padding: 0.625rem 0.875rem;
        font-size: 0.9375rem;
        transition: all 0.25s ease;
    }

    .search-input:focus {
        border-color: #0f766e;
        box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
    }

    /* Section Titles */
    .section-title {
        font-weight: 600;
        color: #0f766e;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.875rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #e2e8f0;
    }

    /* Master-Detail Layout */
    .master-detail-container {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 1.5rem;
        overflow: hidden;
        flex: 1;
        min-height: 0;
    }

    .master-pane,
    .detail-pane {
        display: flex;
        flex-direction: column;
        min-height: 0;
    }

    .master-pane {
        border-right: 1px solid #e2e8f0;
        padding-right: 1.5rem;
    }

    /* Category List */
    .category-list {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        flex: 1;
        min-height: 0;
        max-height: 500px;
    }

    .category-item {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        border-radius: 0.375rem;
        background: white;
        border: 1px solid transparent;
        cursor: pointer;
        transition: all 0.25s ease;
    }

    .category-item:hover {
        background: #faf9f7;
        border-color: #e2e8f0;
    }

    .category-item.active {
        background: #0f766e;
        border-color: #0f766e;
        box-shadow: 0 2px 4px rgba(15, 118, 110, 0.2);
    }

    .category-icon {
        margin-right: 0.75rem;
        color: #64748b;
        font-size: 1rem;
    }

    .category-item.active .category-icon {
        color: white;
    }

    .category-name {
        font-weight: 500;
        color: #1e293b;
        font-size: 0.9375rem;
        flex: 1;
    }

    .category-item.active .category-name {
        color: white;
    }

    /* Map Pages List */
    .map-page-list {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        flex: 1;
        min-height: 0;
        max-height: 500px;
    }

    .map-page-item {
        display: flex;
        align-items: center;
        padding: 0.875rem 1rem;
        border: 1px solid #e2e8f0;
        border-radius: 0.375rem;
        background: white;
        cursor: pointer;
        transition: all 0.25s ease;
    }

    .map-page-item:hover {
        border-color: #0f766e;
        background: #faf9f7;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
    }

    .map-page-item.active {
        border-color: #0f766e;
        background: #f0fdfa;
        border-width: 2px;
    }

    .map-icon {
        margin-right: 0.75rem;
        color: #64748b;
        font-size: 1rem;
        flex-shrink: 0;
    }

    .map-page-item.active .map-icon {
        color: #0f766e;
    }

    .map-page-name {
        font-weight: 500;
        color: #1e293b;
        font-size: 0.9375rem;
        flex: 1;
    }

    .map-page-item.active .map-page-name {
        color: #0f766e;
        font-weight: 600;
    }

    .check-icon {
        color: #0f766e;
        font-size: 1.125rem;
        margin-left: 0.5rem;
    }

    /* Search Results */
    .search-results-list {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-height: 500px;
    }

    .search-result-item {
        padding: 1rem;
        border: 1px solid #e2e8f0;
        border-radius: 0.375rem;
        background: white;
        cursor: pointer;
        transition: all 0.25s ease;
    }

    .search-result-item:hover {
        border-color: #0f766e;
        background: #faf9f7;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
    }

    /* Welcome Modal Styling */
    .welcome-modal .modal-dialog {
        max-width: 900px;
    }

    .welcome-modal .modal-body {
        padding: 0;
    }

    .welcome-content {
        font-size: 0.9375rem;
        line-height: 1.7;
        color: #475569;
        max-height: 60vh;
        overflow-y: auto;
        padding: 1.75rem;
    }

    .welcome-content .authors {
        font-size: 0.875rem;
        color: #64748b;
        font-weight: 500;
        margin-bottom: 0.5rem;
    }

    .welcome-content .subtitle {
        font-size: 1.125rem;
        font-weight: 600;
        color: #0f766e;
        margin-bottom: 1.5rem;
    }

    .welcome-content .section-heading {
        font-size: 1rem;
        font-weight: 600;
        color: #0f766e;
        margin-top: 2rem;
        margin-bottom: 1rem;
        padding-top: 1rem;
        border-top: 2px solid #e2e8f0;
    }

    .welcome-content p {
        margin-bottom: 1rem;
        text-align: justify;
    }

    .welcome-content .border-top {
        border-color: #e2e8f0 !important;
    }

    .welcome-content strong {
        color: #0f766e;
    }

    .dont-show-checkbox {
        margin: 0;
        font-size: 0.9375rem;
        color: #64748b;
    }

    .welcome-modal .modal-footer {
        background: #faf9f7;
        border-top: 1px solid #e2e8f0;
    }

    @media (max-width: 991px) {
        .elegant-navbar {
            padding: 0.75rem 1rem;
        }

        #nav .btn {
            margin: 0.25rem 0;
            width: 100%;
        }

        .nav-selection-btn {
            width: 100%;
            max-width: none;
            justify-content: center;
        }

        .navigation-modal .modal-dialog {
            max-width: 100%;
            margin: 0.5rem;
        }

        .master-detail-container {
            grid-template-columns: 1fr;
            gap: 1rem;
        }

        .master-pane {
            border-right: none;
            border-bottom: 1px solid #e2e8f0;
            padding-right: 0;
            padding-bottom: 1rem;
        }

        .category-list {
            max-height: 200px;
        }
    }

    /* Mode Toggle Buttons */
    .mode-toggle-btn {
        font-weight: 500;
        padding: 0.625rem 1rem;
        border-radius: 0.375rem;
        transition: all 0.25s ease;
    }

    .mode-toggle-btn svg {
        margin-right: 0.5rem;
    }

    .mode-toggle-btn.btn-primary {
        background-color: #0f766e;
        border-color: #0f766e;
    }

    .mode-toggle-btn.btn-outline-secondary {
        color: #64748b;
        border-color: #cbd5e1;
    }

    .mode-toggle-btn.btn-outline-secondary:hover {
        background-color: #f8fafc;
        border-color: #94a3b8;
        color: #475569;
    }

    /* Question Chooser */
    .question-chooser-container {
        min-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    @media (max-width: 576px) {
        .navigation-modal .modal-body {
            padding: 1rem;
        }

        .master-detail-container {
            gap: 0.75rem;
        }

        .mode-toggle-btn {
            font-size: 0.875rem;
            padding: 0.5rem 0.75rem;
        }

        .mode-toggle-btn svg {
            display: none;
        }
    }
</style>